import { useApolloClient } from '@apollo/react-hooks'
import { PERMITTED_FILE_TYPES, compressFile, normalizeFileMimeType, uploadFile } from '@paintscout/util/s3'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDropzone } from 'react-dropzone'

import { getFeature } from '@paintscout/util/builder'
import { useSnackbar } from 'notistack'
import { useClientOptions } from '../../../providers/ClientOptionsProvider'
import type { FileDropAreaProps } from '../../FileDropArea'
import { useCompanyId } from '../../../hooks/useCompanyId'

export default function useFileDropArea({
  onUpload,
  onUploadStart,
  onPartialUpload,
  onPartialUploadError,
  docId,
  s3MediaUploadEnabled,
  ...otherProps
}: Omit<FileDropAreaProps, 'children'>) {
  const apolloClient = useApolloClient()
  const { enqueueSnackbar } = useSnackbar()
  const { options } = useClientOptions()
  const s3UploadFeatureEnabled = getFeature({ options, path: 's3ImageUpload.enabled' })
  const [loading, setLoading] = useState(false)
  const { maxHeight, maxWidth } = otherProps

  // #22526: resolves to `clientId` in customer-view, which mounts no `UserProvider`. See `useCompanyId`.
  const companyId = useCompanyId()

  /**
   * The caller's callbacks, kept current without putting them in `onDrop`'s dep array. Callers pass
   * inline arrow functions, so listing them would re-create `onDrop` on every parent render. They are
   * only ever invoked, never compared, so a ref is the correct home for them.
   */
  const callbacks = useRef({ onUpload, onUploadStart, onPartialUpload, onPartialUploadError })
  useEffect(() => {
    callbacks.current = { onUpload, onUploadStart, onPartialUpload, onPartialUploadError }
  }, [onUpload, onUploadStart, onPartialUpload, onPartialUploadError])

  const onDrop = useCallback(
    async (rawFiles: File[]) => {
      // Some browsers (confirmed on Chrome/macOS) hand HEIC/HEIF files to the page with an
      // empty `file.type`, which would otherwise fail this MIME check even though
      // PERMITTED_FILE_TYPES explicitly allows `image/heic`/`image/heif`. Backfilling the type
      // from the filename extension first keeps this gate accurate for those files.
      const uploadFiles = rawFiles.map(normalizeFileMimeType)
      if (uploadFiles.find((file) => !PERMITTED_FILE_TYPES.includes(file.type))) {
        enqueueSnackbar('Unable to upload file type.', { variant: 'error' })
        return false
      }
      if (callbacks.current.onUploadStart) {
        callbacks.current.onUploadStart()
      }
      setLoading(true)

      const allUploadRes = []
      for (let i = 0; i < uploadFiles.length; i++) {
        try {
          const file = uploadFiles[i]
          const compressedFile = await compressFile({
            file,
            maxHeight,
            maxWidth
          })

          const uploadedFile = await uploadFile({
            file: compressedFile,
            apolloClient,
            uploadOptions: {
              companyId,
              docId,
              s3UploadFeatureEnabled,
              s3MediaUploadEnabled
            }
          })

          if (callbacks.current.onPartialUpload) {
            callbacks.current.onPartialUpload({ totalFiles: uploadFiles.length, index: i })
          }

          allUploadRes.push(uploadedFile)
        } catch (err) {
          if (callbacks.current.onPartialUploadError) {
            callbacks.current.onPartialUploadError({ totalFiles: uploadFiles.length, index: i })
          }
          allUploadRes.push(err)
        }
      }

      setLoading(false)

      callbacks.current.onUpload(allUploadRes)
      // The empty dep array pinned the first render's values, so `companyId` stayed `undefined` even after
      // the provider resolved it (customer-view fetches the quote before it knows the company). Only the
      // values that decide WHERE a file lands are deps; the caller's callbacks live in `callbacks` above.
    },
    [apolloClient, companyId, docId, s3UploadFeatureEnabled, s3MediaUploadEnabled, maxHeight, maxWidth, enqueueSnackbar]
  )

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true
  })

  return { getRootProps, getInputProps, isDragActive, open, loading }
}
